import type { ProductDetailPageFragmentFragment } from "@commerce-frontend/types";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import { cn } from "@commerce-frontend/ui/helpers/styles";
import { useTranslations } from "next-intl";

type Props = {
	materials: ProductDetailPageFragmentFragment["materials"];
};

export const ProductMaterials = ({ materials }: Props) => {
	const t = useTranslations("ProductMaterials");

	if (!materials) {
		return;
	}

	const { base, filling, lining, outside } = materials;

	const materialItems = [
		{ label: t("base", { material: base }), value: base },
		{ label: t("filling", { material: filling }), value: filling },
		{ label: t("lining", { material: lining }), value: lining },
		{ label: t("outside", { material: outside }), value: outside },
	].filter((item) => item.value);

	if (materialItems.length === 0) {
		return null;
	}

	return (
		<div className="lg:w-1/2">
			<div className="size-12 bg-gray-100 flex items-center justify-center rounded-full mb-2xl">
				<Icon icon="pie-chart-03" />
			</div>
			<h3 className="display-xs mb-2xl font-semibold lg:display-sm">{t("title")}</h3>
			<p className="text-gray-600 mb-4xl lg:text-lg">{t("subtitle")}</p>
			<ul aria-label={t("title")} className={cn("flex flex-col gap-xl lg:gap-2xl")}>
				{materialItems.map((item, index) => (
					<MaterialItem key={index}>{item.label}</MaterialItem>
				))}
			</ul>
		</div>
	);
};

const MaterialItem = ({ children }: { children?: React.ReactNode }) => (
	<li className="flex items-center gap-md text-gray-600 lg:text-lg">
		<div className="mx-md size-[14px] bg-gray-600 rounded-full lg:size-[18px] shrink-0" />
		{children}
	</li>
);
